<template>
  <Portal>
    <ModalComponent :show="true" @close="$emit('close')">
      <Card :title="$t('settings')" class="shop-item-settings">
        <template #header>
          <ProductCard :value="value.product" theme="list" />
        </template>
        <template #default>
          <div class="shop-item-settings__fields" v-auto-animate>
            <template v-if="!disabledSettings.includes('inquiries')">
              <CheckboxComponent v-model="data.acceptInquiries" theme="toggle" :label="$t('acceptInquiriesLabel')" />
            </template>
            <template v-if="!disabledSettings.includes('conditions')">
              <CheckboxComponent v-model="conditionsEnabled" theme="toggle" :label="$t('shopProductConditionsLabel')" />
              <Conditions v-if="conditionsEnabled" v-model="data.conditions" />
            </template>
            <template v-if="!disabledSettings.includes('referencePropertyChanges')">
              <CheckboxComponent
                v-model="referencePropertyChangesEnabled"
                theme="toggle"
                :label="$t('shopProductPropertyChangesLabel')"
              />
              <ReferencePropertyChanges
                v-if="referencePropertyChangesEnabled"
                v-model="data.referencePropertyChanges"
              />
            </template>
            <template v-if="!disabledSettings.includes('pricing')">
              <CheckboxComponent
                v-model="pricingOverrideEnabled"
                theme="toggle"
                :label="$t('shopProductPriceOverrideLabel')"
              />
              <Pricing
                v-if="pricingOverrideEnabled"
                v-model="data.overrides.pricing"
                :validator="validator"
                validator-prefix="overrides.pricing"
              />
            </template>
            <template v-if="!disabledSettings.includes('quantity')">
              <CheckboxComponent
                v-model="data.quantity.enabled"
                theme="toggle"
                :label="$t('shopProductQuantityLabel')"
              />
              <div v-if="data.quantity.enabled" class="product-quantity-settings">
                <InputComponent
                  v-model.number="data.quantity.min"
                  :label="labels['quantity.min']"
                  required
                  :placeholder="$t('typeHere') + '...'"
                  :is-valid="validator.fields['quantity.min'].valid"
                  :validation-message="validator.fields['quantity.min'].error"
                />
                <InputComponent
                  v-model.number="data.quantity.max"
                  :label="labels['quantity.max']"
                  required
                  :placeholder="$t('typeHere') + '...'"
                  :is-valid="validator.fields['quantity.max'].valid"
                  :validation-message="validator.fields['quantity.max'].error"
                />
              </div>
            </template>
          </div>
        </template>
        <template #footer>
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="$emit('close')" />
          <ButtonComponent
            :label="$t('confirm')"
            :icon="['fal', 'angle-right']"
            theme="primary"
            :disabled="!validator.passes"
            @click="submit"
          />
        </template>
      </Card>
    </ModalComponent>
  </Portal>
</template>

<script>
import Card from '@/components/Card'
import { ModalComponent } from 'vue-elder-modal'
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent } from 'vue-elder-checkbox'
import Portal from '@/components/Portal'
import Pricing from '@/components/Product/Pricing'
import Conditions from '@/components/Shop/Conditions/ConditionsInput'
import ReferencePropertyChanges from '@/components/Shop/ReferencePropertyChanges'
import ProductCard from '@/components/Product/Card'

import Validator from '@/mixins/validator-v2'
import Model from '@/mixins/Model'
import { Currency } from '@/filters'

export default {
  mixins: [
    Model({
      source: 'value',
      model: {
        overrides: {
          pricing: null,
        },
        conditions: {
          if: [],
          ifnot: [],
        },
        referencePropertyChanges: [],
        quantity: {
          enabled: false,
          min: 1,
          max: null,
        },
        acceptInquiries: false,
      },
    }),
    Validator({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
  ],
  props: {
    value: Object,
    disabledSettings: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    conditionsEnabled(state) {
      if (!state) this.data.conditions = { if: [], ifnot: [] }
    },
    referencePropertyChangesEnabled(state) {
      if (!state) this.data.referencePropertyChanges = []
    },
  },
  data() {
    return {
      conditionsEnabled: false,
      referencePropertyChangesEnabled: false,
    }
  },
  computed: {
    labels() {
      return {
        'quantity.min': this.$t('minimumQuantity'),
        'quantity.max': this.$t('maximumQuantity'),
      }
    },
    rules() {
      let base = {}

      if (this.data.quantity.enabled)
        base = {
          ...base,
          'quantity.min': `required|integer|min:1`,
          'quantity.max': `required|integer|min:${this.data.quantity.min}`,
        }

      if (this.pricingOverrideEnabled)
        base = {
          ...base,
          'overrides.pricing.value': 'required_unless:overrides.pricing.type,request|min:0',
          'overrides.pricing.margin.value': 'required_unless:overrides.pricing.type,request|min:0',
        }

      if (this.referencePropertyChangesEnabled) {
        base = {
          ...base,
          'referencePropertyChanges.*.delta': 'required|numeric',
        }
      }

      return base
    },
    pricingOverrideEnabled: {
      get() {
        return Boolean(this.data.overrides.pricing)
      },
      set(state) {
        if (state) return (this.data.overrides.pricing = structuredClone(this.value.product.pricing))
        else return (this.data.overrides.pricing = null)
      },
    },
  },
  methods: {
    Currency,
    submit() {
      if (!this.validator.passes) return

      this.$emit('input', this.getData())
    },
  },
  created() {
    if (this.value.conditions.if.length || this.value.conditions.ifnot.length) this.conditionsEnabled = true
    if (this.value.referencePropertyChanges.length) this.referencePropertyChangesEnabled = true
  },
  components: {
    Card,
    ModalComponent,
    InputComponent,
    CheckboxComponent,
    Portal,
    Pricing,
    Conditions,
    ProductCard,
    ReferencePropertyChanges,
  },
}
</script>

<style lang="scss">
.shop-item-settings {
  width: 100%;
  max-width: 850px;

  & > .card__header {
    min-height: unset;
    display: flex;
    align-items: unset;

    .product-card .card__header {
      height: 4rem;
      width: 4rem;
    }

    img {
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-md);
    }

    .product-card__subtitle {
      display: none;
    }
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .product-quantity-settings {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  .alert__content {
    white-space: pre-line;
  }

  .elder-checkbox {
    &--theme-toggle {
      flex-direction: row-reverse;
      gap: 0.5rem;

      .elder-checkbox__box {
        margin-right: 0;
      }
      .elder-checkbox__label {
        flex-grow: 1;
      }
    }
  }
}
</style>
